import { WorkExperienceBullet, WorkExperienceRole, WorkExperienceCompany } from '@applicator/shared'

export function reorderItemsById<T extends { id: string }>(items: T[], draggedId: string, targetIndex: number): T[] {
  const draggedIndex = items.findIndex((item) => item.id === draggedId)
  if (draggedIndex === -1 || draggedIndex === targetIndex) return items
  const newArray = [...items]
  const [draggedItem] = newArray.splice(draggedIndex, 1)
  const adjustedIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex
  newArray.splice(adjustedIndex, 0, draggedItem)
  return newArray
}

export function reorderRolesInCompany(
  company: WorkExperienceCompany,
  draggedRoleId: string,
  targetIndex: number,
): WorkExperienceCompany {
  return {
    ...company,
    roles: reorderItemsById(company.roles || [], draggedRoleId, targetIndex),
  }
}

export function reorderWorkExperienceCompanies(
  companies: WorkExperienceCompany[],
  draggedCompanyId: string,
  targetIndex: number,
): WorkExperienceCompany[] {
  return reorderItemsById(companies, draggedCompanyId, targetIndex)
}

// Helper function to check if reorder is needed
export function isReorderNeeded(draggedIndex: number, targetIndex: number): boolean {
  return draggedIndex !== -1 && draggedIndex !== targetIndex
}

// Helper function to calculate adjusted target index
export function calculateAdjustedTargetIndex(draggedIndex: number, targetIndex: number): number {
  return draggedIndex < targetIndex ? targetIndex - 1 : targetIndex
}

// Helper function to find dragged bullet index
export function findDraggedBulletIndex(bulletPoints: WorkExperienceBullet[], draggedBulletId: string): number {
  return bulletPoints.findIndex((b) => b.id === draggedBulletId)
}

// Helper function to create new array without dragged bullet
export function removeDraggedBullet(
  bulletPoints: WorkExperienceBullet[],
  draggedIndex: number
): { newArray: WorkExperienceBullet[]; draggedBullet: WorkExperienceBullet } {
  const newArray = [...bulletPoints]
  const [draggedBullet] = newArray.splice(draggedIndex, 1)
  return { newArray, draggedBullet }
}

// Helper function to insert bullet at target index
export function insertBulletAtTargetIndex(
  bulletPoints: WorkExperienceBullet[],
  draggedBullet: WorkExperienceBullet,
  targetIndex: number
): WorkExperienceBullet[] {
  const newArray = [...bulletPoints]
  newArray.splice(targetIndex, 0, draggedBullet)
  return newArray
}

// Helper function to reorder bullets in a role
export function reorderBulletsInRole(
  bulletPoints: WorkExperienceBullet[],
  draggedBulletId: string,
  targetIndex: number
): WorkExperienceBullet[] {
  const draggedIndex = findDraggedBulletIndex(bulletPoints, draggedBulletId)
  if (!isReorderNeeded(draggedIndex, targetIndex)) {
    return bulletPoints
  }

  const { newArray, draggedBullet } = removeDraggedBullet(bulletPoints, draggedIndex)
  const adjustedTargetIndex = calculateAdjustedTargetIndex(draggedIndex, targetIndex)
  return insertBulletAtTargetIndex(newArray, draggedBullet, adjustedTargetIndex)
}

// Helper function to update role with reordered bullets
export function updateRoleWithReorderedBullets(
  role: WorkExperienceRole,
  draggedBulletId: string,
  targetIndex: number
): WorkExperienceRole {
  const bulletPoints = reorderBulletsInRole([...(role.bulletPoints || [])], draggedBulletId, targetIndex)
  return {
    ...role,
    bulletPoints,
  }
}

// Helper function to update role in company for bullet reorder
export function updateRoleInCompanyForBulletReorder(
  company: WorkExperienceCompany,
  roleId: string,
  draggedBulletId: string,
  targetIndex: number
): WorkExperienceCompany {
  return {
    ...company,
    roles: (company.roles || []).map((role) =>
      role.id === roleId ? updateRoleWithReorderedBullets(role, draggedBulletId, targetIndex) : role
    ),
  }
}
